import basicInfoAtom from "@/atoms/basic-info"
import { EmptyState } from "@/components/feedback/EmptyState"
import { OrderPageLoading } from "@/components/loaders"
import { OrderRatingSheet, StarPicker } from "@/components/orders/OrderRatingSheet"
import {
  Button,
  ButtonAnchor,
  Card,
  DescriptionList,
  Divider,
  Heading,
  Icon,
  KeyValueRow,
  PageContainer,
  Price,
  RatingStars,
  SectionHeader,
  SegmentedProgress,
  Text,
  Timeline,
  TimelineStep,
} from "@/components/ui"
import { OrderItemLine, OrderItemList } from "@/components/orders/OrderItemLine"
import type { OrderFeedback } from "@/domain/order-feedback/order-feedback"
import {
  fetchTrackedOrder,
  isTrackedOrderFinished,
  milestoneIndex,
  milestonesFor,
  submitTrackedOrderFeedback,
  TRACKING_POLL_MS,
  type TrackedOrder,
} from "@/domain/order-tracking/order-tracking"
import { ReferralCard } from "@/components/marketing/ReferralCard"
import { followOrderWhatsAppUrl } from "@/domain/marketing/follow-order"
import { usePageBackground } from "@/hooks/use-page-background"
import { Clock, OrderIcon, Pin, WhatsappLogo } from "@/icons"
import { feedbackMessage } from "@/lib/i18n/feedback-messages"
import { getStatusLabel } from "@/lib/order-status"
import { buildWhatsAppUrl } from "@/lib/whatsapp-link"
import type { OrderStatusType } from "@/types/order-status"
import { format, isSameDay } from "date-fns"
import { useAtomValue } from "jotai"
import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router-dom"

type PageState =
  | { kind: "loading" }
  | { kind: "notFound" }
  | { kind: "error" }
  | { kind: "ok"; order: TrackedOrder }

function clock(iso: string): string {
  return format(new Date(iso), "HH:mm")
}

function etaLabel(order: TrackedOrder): string | null {
  if (!order.etaStart) return null
  const start = new Date(order.etaStart)
  const day = isSameDay(start, new Date()) ? "" : `${format(start, "dd/MM")} `
  if (order.etaEnd) return `${day}${clock(order.etaStart)}–${clock(order.etaEnd)}`
  return `${day}${clock(order.etaStart)}`
}

/** Login-free tracking page `/p/:token`: live status, timeline, ETA, masked address, items and the rating CTA after delivery. */
export default function TrackOrderPage() {
  const { token = "" } = useParams<{ token: string }>()
  const [state, setState] = useState<PageState>({ kind: "loading" })
  usePageBackground()

  const load = useCallback(async () => {
    const result = await fetchTrackedOrder(token)
    setState((prev) => {
      if (result.kind === "error" && prev.kind === "ok") return prev
      return result.kind === "ok" ? { kind: "ok", order: result.order } : { kind: result.kind }
    })
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const live = state.kind === "ok" && !isTrackedOrderFinished(state.order)
  useEffect(() => {
    if (!live) return
    const tick = () => {
      if (document.visibilityState === "visible") void load()
    }
    const interval = setInterval(tick, TRACKING_POLL_MS)
    document.addEventListener("visibilitychange", tick)
    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", tick)
    }
  }, [live, load])

  if (state.kind === "loading") return <OrderPageLoading />
  if (state.kind === "notFound") {
    return (
      <EmptyState
        title="Este link de acompanhamento expirou"
        description="Os links valem até 7 dias depois da entrega. Se precisar, fale com a loja."
      />
    )
  }
  if (state.kind === "error") {
    return (
      <EmptyState
        title="Não deu pra carregar seu pedido"
        description="Confira sua conexão e tente de novo."
        action={
          <Button size="lg" fullWidth onClick={() => void load()}>
            Tentar de novo
          </Button>
        }
      />
    )
  }

  const order = state.order
  const remember = (feedback: OrderFeedback) =>
    setState({ kind: "ok", order: { ...order, feedback, rateable: false } })

  return (
    <PageContainer top="header-gap" bottom="chrome" className="flex max-w-2xl flex-col gap-4">
      <StatusCard order={order} />
      <RatingCard token={token} order={order} onRated={remember} />
      <TimelineCard order={order} />
      {order.maskedAddress ? (
        <Card as="section" padding="lg" aria-label="Endereço de entrega">
          <SectionHeader icon={Pin} title="Entrega em" spacing="sm" />
          <Text weight="semibold" tone="default">
            {order.maskedAddress}
          </Text>
        </Card>
      ) : null}
      <ItemsCard order={order} />
      <FollowOnWhatsAppCard displayId={order.displayId} />
      <ReferralCard storeName={order.storeName} />
      <ContactCard storeName={order.storeName} displayId={order.displayId} />
    </PageContainer>
  )
}

function StatusCard({ order }: { order: TrackedOrder }) {
  const milestones = milestonesFor(order.type)
  const reached = milestoneIndex(order.status, order.type)
  const cancelled = reached < 0
  const eta = etaLabel(order)
  const finished = isTrackedOrderFinished(order)

  return (
    <Card as="section" padding="lg" aria-live="polite">
      <Text size="sm" tone="muted">
        {order.storeName} · Pedido #{order.displayId}
      </Text>
      <Heading level={1} size={{ base: "headline", sm: "page" }} leading="tight" tone={cancelled ? "danger" : "default"} className="mt-1">
        {getStatusLabel(order.status as OrderStatusType, order.type)}
      </Heading>
      {eta && !finished ? (
        <Text tone="muted" className="mt-1">
          {order.type === "DELIVERY" ? "Previsão de entrega" : "Previsão"}:{" "}
          <Text as="strong" tone="default">
            {eta}
          </Text>
        </Text>
      ) : null}

      {!cancelled ? (
        <div className="mt-5 flex flex-col gap-2">
          <SegmentedProgress
            segments={milestones.length}
            current={reached}
            size="sm"
            pulseCurrent={!finished}
            label="Progresso do pedido"
          />
          <ol className="grid grid-cols-4 gap-1" aria-label="Andamento do pedido">
            {milestones.map((m, i) => {
              const done = i <= reached
              const current = i === reached && !finished
              return (
                <Text
                  as="li"
                  key={m.key}
                  size={{ base: "xs", sm: "sm" }}
                  weight={done ? "semibold" : undefined}
                  tone={done ? "default" : "subtle"}
                  truncate
                  aria-current={current ? "step" : undefined}
                >
                  {m.label}
                </Text>
              )
            })}
          </ol>
        </div>
      ) : (
        <Text tone="muted" className="mt-3">
          Se você já pagou, a loja cuida do estorno.
        </Text>
      )}
    </Card>
  )
}

function RatingCard({ token, order, onRated }: { token: string; order: TrackedOrder; onRated: (fb: OrderFeedback) => void }) {
  const [open, setOpen] = useState(false)
  const [preset, setPreset] = useState(0)

  if (order.feedback) {
    return (
      <Card as="section" padding="lg" className="flex items-center justify-center gap-2">
        <Text as="span" size="sm" tone="muted">
          {feedbackMessage("rated")}
        </Text>
        <RatingStars
          value={order.feedback.stars}
          size="sm"
          label={feedbackMessage("starAria", { n: order.feedback.stars })}
        />
      </Card>
    )
  }
  if (!order.rateable) return null

  return (
    <Card as="section" variant="tinted" tone="primary" frame="none" padding="lg" aria-labelledby="track-rate-title">
      <Heading id="track-rate-title" level={2} size="section" align="center">
        {feedbackMessage("detailTitle")}
      </Heading>
      <Text size="sm" tone="muted" align="center" className="mt-1">
        {feedbackMessage("sheetSubtitle")}
      </Text>
      <StarPicker
        value={preset}
        onChange={(stars) => {
          setPreset(stars)
          setOpen(true)
        }}
      />
      <OrderRatingSheet
        orderId={order.displayId}
        displayId={order.displayId}
        open={open}
        initialStars={preset}
        onOpenChange={setOpen}
        onRated={onRated}
        submit={(stars, comment) => submitTrackedOrderFeedback(token, stars, comment)}
      />
    </Card>
  )
}

function TimelineCard({ order }: { order: TrackedOrder }) {
  if (order.timeline.length === 0) return null
  const steps = [...order.timeline].reverse()
  return (
    <Card as="section" padding="lg" aria-label="Histórico do pedido">
      <SectionHeader icon={Clock} title="Histórico" />
      <Timeline order="newest-first">
        {steps.map((step, i) => (
          <TimelineStep
            key={`${step.status}-${step.at}`}
            state={i === 0 ? "current" : "done"}
            title={getStatusLabel(step.status as OrderStatusType, order.type)}
            time={clock(step.at)}
            dateTime={step.at}
          />
        ))}
      </Timeline>
    </Card>
  )
}

function ItemsCard({ order }: { order: TrackedOrder }) {
  return (
    <Card as="section" padding="lg" aria-label="Itens do pedido">
      <SectionHeader icon={OrderIcon} title="Seu pedido" />
      <OrderItemList>
        {order.lines.map((line, i) => (
          <OrderItemLine
            key={`${line.name}-${i}`}
            name={line.name}
            quantity={line.quantity}
            price={line.totalCents > 0 ? line.totalCents / 100 : undefined}
            options={line.options.map((option, position) => ({ key: `${position}-${option}`, name: option }))}
          />
        ))}
      </OrderItemList>
      <Divider className="my-4" />
      <DescriptionList gap="sm">
        <KeyValueRow label="Itens" value={<Price cents={order.itemsCents} />} />
        {order.feesCents > 0 ? (
          <KeyValueRow label="Taxas" value={<Price cents={order.feesCents} free />} />
        ) : null}
        {order.discountCents > 0 ? (
          <KeyValueRow label="Descontos" value={<Price cents={order.discountCents} sign="minus" tone="secondary" />} />
        ) : null}
        <KeyValueRow emphasis="total" label="Total" value={<Price cents={order.totalCents} tone="secondary" />} />
      </DescriptionList>
    </Card>
  )
}

function WhatsAppCard({
  title,
  description,
  href,
  cta,
}: {
  title: string
  description: string
  href: string
  cta: string
}) {
  return (
    <Card as="section" padding="lg" className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <Heading level={2} size="card">
          {title}
        </Heading>
        <Text size="sm" tone="muted">
          {description}
        </Text>
      </div>
      <ButtonAnchor href={href} target="_blank" size="md" className="shrink-0">
        <Icon as={WhatsappLogo} size="md" weight="fill" />
        {cta}
      </ButtonAnchor>
    </Card>
  )
}

function FollowOnWhatsAppCard({ displayId }: { displayId: string }) {
  const basicInfo = useAtomValue(basicInfoAtom)
  const url = followOrderWhatsAppUrl(basicInfo?.contactPhones?.whatsappNumber, displayId)
  if (!url) return null
  return (
    <WhatsAppCard
      title="Receba as atualizações"
      description="Acompanhe o status do pedido direto no seu WhatsApp."
      href={url}
      cta="Acompanhar pelo WhatsApp"
    />
  )
}

function ContactCard({ storeName, displayId }: { storeName: string; displayId: string }) {
  const basicInfo = useAtomValue(basicInfoAtom)
  const url = buildWhatsAppUrl({
    phone: basicInfo?.contactPhones?.whatsappNumber,
    message: `Olá! Sobre o pedido #${displayId}`,
  })
  if (!url) return null
  return (
    <WhatsAppCard
      title="Precisa de ajuda?"
      description={`Fale com ${storeName || "a loja"} pelo WhatsApp.`}
      href={url}
      cta="Falar com a loja"
    />
  )
}
